import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from './axios';

const STORAGE_KEY = 'staty_completed_tours';
const DISMISSED_KEY = 'staty_tours_dismissed';
const baseUrl = `${process.env.API_URL}/api/v1`;

export type TourId =
  | 'create-league'
  | 'create-tournament'
  | 'publish-registration'
  | 'view-site'
  | 'explore-competitions';

interface TourState {
  completedTours: string[];
  toursDismissed: boolean;
  activeTour: TourId | null;
  isLoading: boolean;
}

interface TourActions {
  syncFromServer: (admin: any) => void;
  markCompleted: (tourId: TourId, clerkId: string, getToken: () => Promise<string | null>) => Promise<void>;
  hasCompleted: (tourId: TourId) => boolean;
  dismissTours: (clerkId: string, getToken: () => Promise<string | null>) => Promise<void>;
  setActiveTour: (tourId: TourId | null) => void;
  resetTours: (clerkId: string, getToken: () => Promise<string | null>) => Promise<void>;
}

type TourStore = TourState & TourActions;

const getLocalTours = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getLocalDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const useTourStore = create<TourStore>()(
  immer((set, get) => ({
    completedTours: getLocalTours(),
    toursDismissed: getLocalDismissed(),
    activeTour: null,
    isLoading: false,

    syncFromServer: (admin) => {
      const tours = admin?.onboarding?.completedTours || [];
      const dismissed = admin?.onboarding?.toursDismissed || false;
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tours));
      if (dismissed) {
        localStorage.setItem(DISMISSED_KEY, 'true');
      }
      
      set((state) => {
        state.completedTours = tours;
        state.toursDismissed = dismissed;
      });
    },

    markCompleted: async (tourId, clerkId, getToken) => {
      const currentTours = get().completedTours;
      if (currentTours.includes(tourId)) return;

      const updated = [...new Set([...currentTours, tourId])];
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      set((state) => {
        state.completedTours = updated;
        state.activeTour = null;
      });

      try {
        const token = await getToken();
        await axios.patch(
          `${baseUrl}/admins/${clerkId}/onboarding`,
          {
            onboarding: {
              completedTours: updated,
              toursDismissed: get().toursDismissed,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to sync tour completion to server:', error);
      }
    },

    hasCompleted: (tourId) => {
      return get().completedTours.includes(tourId);
    },

    dismissTours: async (clerkId, getToken) => {
      localStorage.setItem(DISMISSED_KEY, 'true');
      set((state) => {
        state.toursDismissed = true;
      });

      try {
        const token = await getToken();
        await axios.patch(
          `${baseUrl}/admins/${clerkId}/onboarding`,
          {
            onboarding: {
              completedTours: get().completedTours,
              toursDismissed: true,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to sync tour dismissal to server:', error);
      }
    },

    setActiveTour: (tourId) => {
      set((state) => {
        state.activeTour = tourId;
      });
    },

    resetTours: async (clerkId, getToken) => {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DISMISSED_KEY);
      set((state) => {
        state.completedTours = [];
        state.toursDismissed = false;
      });

      try {
        const token = await getToken();
        await axios.patch(
          `${baseUrl}/admins/${clerkId}/onboarding`,
          {
            onboarding: {
              completedTours: [],
              toursDismissed: false,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to reset tours on server:', error);
      }
    },
  }))
);

export default useTourStore;
